/**
 * The strip of a Free organisation on the Pro trial (#2816, «Показ пробы»):
 * how many days are left and until when the Pro features are open. Mounted in
 * both shells — the web app (`AppShell`) and the Bitrix24/Uspacy iframe — and
 * read from the flag map (`useProTrial`), which each shell fills from its own
 * source (`/feature-flags`, the embedded session).
 *
 * The CTA follows the lock screen's rule (`usePlansCta`): the plans link for a
 * role that can open billing, otherwise «ask the account owner»; in the iframe
 * every role reads the same — the owner changes the plan in the web app.
 *
 * Shown only while the trial runs. Before it starts and after it ends the lock
 * screens say so (`ProTrialLockNote`): the strip on every screen would be noise
 * for an organisation that has nothing open.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { useProTrial } from '@/contexts/FeatureFlagsContext';
import { usePlansCta } from '@/hooks/useTierPlanCta';
import { PLANS_HREF } from '@/lib/plans-href';
import { msToNextDayChange, trialDaysLeft } from '@/lib/pro-trial';
import { formatDateTimeLocale } from '@/lib/utils';

export function ProTrialBanner() {
  const { t } = useTranslation('common');
  const proTrial = useProTrial();
  const { canOpenPlans, embedded } = usePlansCta();
  const endsAt = proTrial?.state === 'active' ? proTrial.endsAt : null;
  // Redraw when the count drops a day: a tab left open must not keep
  // yesterday's number. The tick only re-renders; the count reads the clock.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!endsAt) return;
    const delay = msToNextDayChange(endsAt, new Date());
    if (delay === null) return;
    const timer = setTimeout(() => setTick((n) => n + 1), delay);
    return () => clearTimeout(timer);
  }, [endsAt, tick]);
  if (!endsAt) return null;

  return (
    <div
      role="status"
      data-testid="pro-trial-banner"
      className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-info-dim px-4 py-2"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium text-info-fg">
            {t('proTrial.banner.title', { count: trialDaysLeft(endsAt, new Date()) })}
          </p>
          <p className="text-xs text-info-fg">
            {t('proTrial.banner.until', { date: formatDateTimeLocale(endsAt) })}
            {!canOpenPlans &&
              ` ${t(
                embedded ? 'proTrial.banner.askOwnerEmbedded' : 'proTrial.banner.askOwner'
              )}`}
          </p>
        </div>
      </div>
      {canOpenPlans && (
        <a
          href={PLANS_HREF}
          className="text-sm font-medium text-info-fg underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('featureGate.tier.cta')}
        </a>
      )}
    </div>
  );
}
